import React, {FC, useCallback, useEffect, useState} from "react";
import {GridColDef, GridColumnGroupingModel, GridRenderCellParams} from "@mui/x-data-grid";
import {useStyles} from "./styles";
import {Link} from "react-router-dom";
import {CustomDataGrid, StatusTag, BigX} from "../../common";
import {useDispatch, useSelector} from "react-redux";
import {setSnackBar} from "../../../stores/ui/ui-action";
import {
    getActionRequiredCompanies,
    getActionedCompanies,
    updateDisconnectMgmtStatus
} from "../../../services/bff-service";
import {DisconnectionManagementFilters} from "./disconnection-management-filters";
import {
    Box,
    Tooltip,
    Button,
    Chip,
    IconButton,
    Typography,
    Backdrop
} from "@material-ui/core";
import InactivateCompanyInstructions from "./inactive-dialog";
import {useLinkWithSrc} from "../../../shared/hooks/use-route-navigator";
import CONSTANTS from "../../../utils/constants";
import {useLocation} from 'react-router-dom';
import ActivateCompanyDialog from "./activate-dialog";
import TooltipContentByLayout from "./tooltip-by-layout";
import {InfoOutlined} from "@material-ui/icons";
import CircularProgress from '@material-ui/core/CircularProgress';
import RenewalCompanyDialog from "./renewal-dialog";
import moment from "moment-timezone";
import {FormControl, InputLabel, MenuItem, Select} from "@mui/material";
import NoResultsIcon from "../../../assets/no_results_icon.svg";
import CompanySearch from "../../common/company-search";
import {Company} from "../../../stores/company/company-state";
import {isValidCIF, isValidCRMEntityID} from "../company-list/list-filters";

const searchSettingOptions = [
    {displayName: 'Search This List', value: 'SEARCH_THIS_LIST'},
    {displayName: 'Search All Companies', value: 'SEARCH_ALL_COMPANIES'},
]

let managedCompaniesLookUp = new Map();

export const DisconnectionManagement: FC<any> = () => {
    const styles = useStyles();
    const dispatch = useDispatch();
    const [isLoading, setLoading] = useState(true);

    const [searchSetting, setSearchSetting] = useState("SEARCH_THIS_LIST"); // Search Setting
    const [displayNoResultState, setDisplayNoResultState] = useState(false); // Used to display the No result icon when the searchText is less-than 3 char length and the searchSetting is "Search All Companies".

    const [managedCompaniesResponse, setManagedCompaniesResponse] = useState([]);
    const [filteredManagedCompaniesResponse, setFilteredManagedCompaniesResponse] = useState([]); // Filtered list of action required companies
    const [pageSize, setPageSize] = useState<number>(50); // Default page size to 50

    const [openInactivateCompanyDialog, setOpenInactivateCompanyDialog] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [openActivateCompanyDialog, setOpenActivateCompanyDialog] = useState(false);
    const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
    const [displayNullState, setDisplayNullState] = useState(false);
    const linkWithSrcInstance = useLinkWithSrc();
    const {search} = useLocation();
    const user = useSelector((state: any) => state.user);
    const filterType = new URLSearchParams(search).get('tab') || CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED;
    const [isCircularProgressLoading, setCircularProgressLoading] = useState(false);


    // This will get invoked when the tab get changed.
    useEffect(() => {
        setDisplayNullState(false);//reset the null state as both tabs have different null state screens
        fetchManagedCompaniesDetails();
        setSearchSetting('SEARCH_THIS_LIST');
        return () => {
            managedCompaniesLookUp = new Map();
        }
    }, [filterType]);

    useEffect(() => {
        if (searchSetting === 'SEARCH_ALL_COMPANIES') {
            setDisplayNoResultState(true)
        } else {
            setDisplayNoResultState(false);
        }
    }, [searchSetting]);

    useEffect(() => {
        if (searchSetting === 'SEARCH_ALL_COMPANIES') {
            setDisplayNoResultState(filteredManagedCompaniesResponse.length === 0)
        }
    }, [filteredManagedCompaniesResponse]);


    const handleChange = ({target: {name, value}}) => {
        switch (name) {
            case 'searchSetting':
                setSearchSetting(value)
                break;
        }
    };

    const getSearchResults = useCallback((filteredResults: Company[]) => {
        if (searchSetting === 'SEARCH_ALL_COMPANIES') {
            let clonedFilteredCompanies = structuredClone(filteredResults);
            clonedFilteredCompanies = clonedFilteredCompanies.map(companyRow => {
                return managedCompaniesLookUp.has(companyRow?.companyId) ? managedCompaniesLookUp.get(companyRow?.companyId) : companyRow;
            });
            setFilteredManagedCompaniesResponse(clonedFilteredCompanies);
            return;
        }
        setFilteredManagedCompaniesResponse(filteredResults);
    }, [searchSetting])

    const handlePageSizeChange = (pageSize: number) => {
        setPageSize(pageSize);
    };

    const handleInactivateCompanyDialogOpen = (company: any) => {
        setSelectedCompany(company);
        setOpenInactivateCompanyDialog(true);
    };

    const handleInactivateCompanyDialogClose = () => {
        setOpenInactivateCompanyDialog(false);
    };

    const handleInactivateCompanyConfirm = async () => {
        try {
            setOpenInactivateCompanyDialog(false);
            setCircularProgressLoading(true);
            await fetchManagedCompaniesDetails();
        } catch (error) {
            dispatch(setSnackBar({
                show: true,
                message: `Failed to update client status (${error.message}). Please try again.`,
                messageType: "error"
            }));
        } finally {
            setCircularProgressLoading(false);
        }
    };

    const handleActivateCompanyDialogOpen = (company: any) => {
        setSelectedCompany(company);
        setOpenActivateCompanyDialog(true);
    };

    const handleActivateCompanyDialogClose = () => {
        setOpenActivateCompanyDialog(false);
    };


    const handleOpenRenewalDialog = (company: any) => {
        setSelectedCompany(company);
        setRenewalDialogOpen(true);
    };

    const handleCloseRenewalDialog = () => {
        setRenewalDialogOpen(false);
    };


    const handleActivateCompanyConfirm = async () => {
        if (!selectedCompany) return;

        try {
            setOpenActivateCompanyDialog(false);
            setCircularProgressLoading(true);
            await updateDisconnectMgmtStatus({
                status: 'active',
                action_id: selectedCompany.action_id,
                company_id: selectedCompany.companyId,
                codat_company_id: selectedCompany.codatCompanyId,
                user_email: user.email
            });
            dispatch(setSnackBar({show: true, message: `Company set to "Active".`, messageType: "success"}));
            await fetchManagedCompaniesDetails();
        } catch (error) {
            dispatch(setSnackBar({
                show: true,
                message: `Failed to update client status (${error.message}). Please try again.`,
                messageType: "error"
            }));
        } finally {
            setCircularProgressLoading(false);
        }
    };

    const FlagChips = ({flags, type, otherComment}: {
        flags: Record<string, string>[];
        type?: "actionRequired" | "actioned"
        otherComment?: string | any;
    }) => {
        return (
            <div className={styles.chipCollection}>
                {flags?.map((flagObj) => {
                        const [flagCode, flagDescription] = Object.entries(flagObj)[0];
                        const toolTipText = CONSTANTS.FLAG_CODES[flagCode] || '';
                        return (
                            <Tooltip key={`${flagCode}`} title={toolTipText} placement="bottom" arrow>
                                <Chip variant={type === "actionRequired" ? "outlined" : "default"} size="small"
                                      className={flagCode === "other" && otherComment ? styles.chipActionedFlagOther : type === "actionRequired" ? styles.chip : styles.chipActioned}
                                      label={
                                          <>
                                              <Typography variant="body2"
                                                          className={flagCode === "other" ? styles.chipTextActionedFlagOther : type === "actionRequired" ? styles.chipText : styles.chipTextActioned}>
                                                  {flagCode === "other" && otherComment
                                                      ? otherComment
                                                      : flagDescription}
                                              </Typography>
                                              <IconButton size="small"
                                                          className={type === "actionRequired" ? styles.iconButton : styles.iconButtonActioned}>
                                                  <InfoOutlined fontSize="small"/>
                                              </IconButton>
                                          </>
                                      }
                                />
                            </Tooltip>
                        );
                    }
                )}
            </div>
        );
    };

    const fetchManagedCompaniesDetails = async () => {
        try {
            setFilteredManagedCompaniesResponse([])
            setManagedCompaniesResponse([]);//to handle internal page refresh
            let companyDetails = [];
            if (filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED) {
                companyDetails = await getActionRequiredCompanies();
                managedCompaniesLookUp = companyDetails.reduce((previousValue, currentValue) => {
                    previousValue.set(currentValue.companyId, currentValue);
                    return previousValue;
                }, managedCompaniesLookUp)
            } else {
                companyDetails = await getActionedCompanies();
            }
            setManagedCompaniesResponse(companyDetails);
            setFilteredManagedCompaniesResponse(companyDetails)
            if (filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED && !companyDetails.length) {
                setDisplayNullState(true);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            dispatch(
                setSnackBar({
                    show: true,
                    message: "Error during setup, please refresh and try again momentarily.",
                    description: error.message,
                    messageType: "error",
                })
            );
        }
    };




    const columnGroupingModel: GridColumnGroupingModel = [
        {
            groupId: "internal_data",
            headerName: "",
            headerClassName: styles.hideRightSeparator,
            children:
                filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED
                    ? [{field: "name"}, {field: "action_flags"}]
                    : [
                        {field: "name"},
                        {field: "actioned_date"},
                        {field: "actioned_by"},
                        {field: "actioned_flags"},
                    ],
        },
        {
            groupId: "lendx",
            headerName: "LendX",
            headerClassName: `${styles.hideRightSeparator} ${styles.rightBorder} ${styles.leftBorder}`,
            children: [{field: "codatConnectionStatus"}, ...(filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED ? [{field: "status"}] : []), {field: "teamCode"},],
            renderHeaderGroup: (params) => (
                <Box display="flex" gridGap="6px" alignItems="center">
                    <Typography>{params.headerName}</Typography>
                    {filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTIONED && <Tooltip
                        color="action"
                        arrow
                        title="The data shown reflects the data present at the time the action was taken."
                    >
                        <InfoOutlined className={styles.tooltipIcon}/>
                    </Tooltip>}
                </Box>
            ),
        },
        {
            groupId: "ctPortfolioMasterReport",
            headerName: "CT Portfolio Master Report",
            headerClassName: `${styles.hideRightSeparator} ${styles.rightBorder}`,
            children: [{field: "ct_client_status"}, {field: "ct_team_code"}],
        },
        {
            groupId: "nCinoCreditPackageReport",
            headerName: "nCino Credit Package Report",
            headerClassName: `${styles.hideRightSeparator} ${styles.rightBorder}`,
            children: [{field: "cp_completed_packages"}, {field: "cp_team_code"}],
        },
        {
            groupId: "updateStatus",
            headerName: "",
            children:
                filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED
                    ? [{field: "inActive"}, {field: "renewal"}]
                    : [{field: "activated"}],
        },
    ];

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Company Legal Name",
            sortable: true,
            flex: 1,
            minWidth: filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED ? 300 : 200,
            maxWidth: 350,
            cellClassName: `${styles.colorBlue} ${styles.doubleStackDataCellAlignment}`,
            headerClassName: styles.hideRightSeparator,
            renderCell: (params: GridRenderCellParams) => {
                let previewCrmEntityIdNCif = [];
                const {crmEntityId, cif} = params?.row;
                if (filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTIONED || searchSetting === 'SEARCH_ALL_COMPANIES')
                    previewCrmEntityIdNCif = [cif];
                else {
                    if (isValidCRMEntityID(crmEntityId)) previewCrmEntityIdNCif.push(`CRM Entity ID: ${crmEntityId}`);
                    if (isValidCIF(cif)) previewCrmEntityIdNCif.push(`CIF: ${cif}`);
                }
                return <>
                    <Tooltip arrow title={<TooltipContentByLayout data={params?.row} filterType={filterType}
                                                                  searchSetting={searchSetting}/>}
                             data-testid={params?.row?.codatCompanyId}>
                          <span className={styles.textOverflow}>
                            {params?.row?.codatCompanyId ? (
                                <Link
                                    to={linkWithSrcInstance(`/svber/data-management/company-profile/${params?.row?.codatCompanyId}`)}>
                                    {" "}
                                    {params.value ?? "NA"}
                                </Link>
                            ) : (
                                params.value ?? "NA"
                            )}
                              <p className={styles.cifTextStyle}>{previewCrmEntityIdNCif.join(', ')}</p>
                          </span>
                    </Tooltip>
                </>
            },
        },
        ...(filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED
            ? [
                {
                    field: "action_flags",
                    headerName: "Flags",
                    minWidth: 243,
                    sortable: false,
                    headerClassName: `${styles.hideRightSeparator} ${styles.rightBorder}`,
                    cellClassName: `${styles.hideRightSeparator} ${styles.rightBorder} ${styles.flagAlignment}`,
                    renderCell: (params: GridRenderCellParams) => <FlagChips flags={params.value}
                                                                             type="actionRequired"/>,
                },
            ]
            : [
                {
                    field: "actioned_date",
                    headerName: "Actioned Date",
                    minWidth: 195,
                    sortable: false,
                    headerClassName: styles.hideRightSeparator,
                    cellClassName: styles.singleStackDataCellAlignment,
                    renderCell: (params: GridRenderCellParams) =>
                        params.value ? (
                            `${moment.tz(params.value as string, "America/Los_Angeles").format("MMM DD YYYY hh:mmA ")}PT`
                        ) : (
                            <p style={{opacity: 0.6}}>N/A</p>
                        ),
                },
                {
                    field: "actioned_by",
                    headerName: "Action Taken",
                    minWidth: 184,
                    sortable: false,
                    headerClassName: styles.hideRightSeparator,
                    cellClassName: styles.doubleStackDataCellAlignment,
                    renderCell: (params: GridRenderCellParams) => (
                        <div>
                            {params.row?.action_status}
                            {params?.row?.actioned_by &&
                                <p className={styles.cifTextStyle}>By {params?.row?.actioned_by}</p>}
                            {params?.row?.client_status === "Pending Renewal" && (
                                <p className={styles.cifTextStyle}>
                    <span>
                      Expected By{" "}
                        {params?.row?.renewal_date
                            ? new Date(params.row.renewal_date).toLocaleDateString("en-US", {
                                month: "2-digit",
                                year: "numeric",
                            })
                            : "-"}
                    </span>
                                </p>
                            )}
                        </div>
                    ),
                },
                {
                    field: "actioned_flags",
                    headerName: "Actioned Flags",
                    width: 368,
                    sortable: false,
                    headerClassName: `${styles.hideRightSeparator} ${styles.rightBorder}`,
                    cellClassName: `${styles.hideRightSeparator} ${styles.rightBorder} ${styles.flagAlignment}`,
                    renderCell: (params: GridRenderCellParams) => <FlagChips flags={params.value}
                                                                             otherComment={params?.row?.comment}
                                                                             type="actioned"/>,
                },
            ]),
        {
            field: "codatConnectionStatus",
            headerName: "Codat Connection",
            width: 145,
            sortable: false,
            headerClassName: styles.hideRightSeparator,
            cellClassName: styles.singleStackDataCellAlignment,
            renderCell: (params: GridRenderCellParams) => (
                <StatusTag caseName="connectionStatus" status={params.value as string}/>
            ),
        },
        ...(filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED
            ? [
                {
                    field: "status",
                    headerName: "Client Status",
                    width: 154,
                    sortable: false,
                    headerClassName: styles.hideRightSeparator,
                    cellClassName: styles.singleStackDataCellAlignment,
                    renderCell: (params: GridRenderCellParams) => (
                        <StatusTag status={params.value as string} caseName="clientStatus">
                            {params.value === "Pending Renewal" ? (
                                <p className={styles.cifTextStyle}>
                    <span>
                      Expected{" "}
                        {params?.row?.renewal_date
                            ? new Date(params.row.renewal_date).toLocaleDateString("en-US", {
                                month: "2-digit",
                                year: "numeric",
                            })
                            : "-"}
                    </span>
                                </p>
                            ) : null}
                        </StatusTag>
                    ),
                },
            ]
            : []),
        {
            field: "teamCode",
            headerName: "Team Code",
            width: 99,
            sortable: false,
            headerClassName: `${styles.hideRightSeparator} ${styles.rightBorder}`,
            cellClassName: `${styles.hideRightSeparator} ${styles.rightBorder} ${styles.singleStackDataCellAlignment}`,
            renderCell: (params: GridRenderCellParams) => <div>{params.value ? params.value : "-"}</div>,
        },
        {
            field: "ct_client_status",
            headerName: "Client Status",
            minWidth: 130,
            sortable: false,
            cellClassName: styles.singleStackDataCellAlignment,
            headerClassName: styles.hideRightSeparator,
            renderCell: (params: GridRenderCellParams) => (
                <StatusTag caseName="clientStatus" status={params.value as string}/>
            ),
        },
        {
            field: "ct_team_code",
            headerName: "Team Code",
            width: 99,
            sortable: false,
            headerClassName: `${styles.hideRightSeparator} ${styles.rightBorder}`,
            cellClassName: `${styles.hideRightSeparator} ${styles.rightBorder} ${styles.singleStackDataCellAlignment}`,
            renderCell: (params: GridRenderCellParams) => <div>{params.value}</div>,
        },
        {
            field: "cp_completed_packages",
            headerName: "Credit Packages",
            width: 133,
            sortable: false,
            headerClassName: styles.hideRightSeparator,
            cellClassName: styles.doubleStackDataCellAlignment,
            renderCell: (params: GridRenderCellParams) =>
                params?.row?.cp_total_packages &&
                params?.row?.cp_completed_packages && (
                    <div>
                        {params.value} Complete
                        <p className={styles.cifTextStyle}>{params?.row?.cp_total_packages || "-"} Total</p>
                    </div>
                ),
        },
        {
            field: "cp_team_code",
            headerName: "Team Code",
            width: 99,
            sortable: false,
            headerClassName: `${styles.hideRightSeparator} ${styles.rightBorder}`,
            cellClassName: `${styles.hideRightSeparator} ${styles.rightBorder} ${styles.singleStackDataCellAlignment}`,
            renderCell: (params: GridRenderCellParams) => <div>{params.value}</div>,
        },
        ...(filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED
            ? [
                {
                    field: "inActive",
                    headerName: "Update Status",
                    width: 117,
                    sortable: false,
                    headerClassName: styles.hideRightSeparator,
                    cellClassName: `${styles.hideSeparatorForLeftButton} ${styles.singleStackDataCellAlignment}`,
                    renderCell: (params: GridRenderCellParams) => (
                        <Button
                            className={styles.button}
                            aria-haspopup="true"
                            variant="text"
                            color="primary"
                            onClick={() => handleInactivateCompanyDialogOpen(params.row)}
                        >
                            INACTIVE
                        </Button>
                    ),
                },
                {
                    field: "renewal",
                    headerName: "",
                    flex: 1,
                    width: 116,
                    sortable: false,
                    headerClassName: styles.hideRightSeparator,
                    cellClassName: `${styles.hideSeparatorForOtherButtons} ${styles.singleStackDataCellAlignment}`,
                    renderCell: (params: GridRenderCellParams) => (
                        <Button
                            className={styles.button}
                            aria-haspopup="true"
                            variant="text"
                            color="primary"
                            onClick={() => handleOpenRenewalDialog(params.row)}
                        >
                            RENEWAL
                        </Button>
                    ),
                },
            ]
            : [
                {
                    field: "activated",
                    headerName: "Update Status",
                    width: 119,
                    sortable: false,
                    headerClassName: styles.hideRightSeparator,
                    cellClassName: `${styles.hideSeparatorForOtherButtons} ${styles.placeItemCenter} ${styles.singleStackDataCellAlignment}`,
                    renderCell: (params: GridRenderCellParams) => (
                        <Button
                            className={styles.button}
                            aria-haspopup="true"
                            variant="text"
                            color="primary"
                            disabled={params?.row?.activated === true}
                            onClick={() => handleActivateCompanyDialogOpen(params.row)}
                        >
                            ACTIVATE
                        </Button>
                    ),
                },
            ]),
    ];

    const renderContent = () => {
        if (displayNullState && searchSetting === 'SEARCH_THIS_LIST') {
            return <BigX sourcePage={"disconnectionManagement:nullState"}/>;
        }
        if (displayNoResultState && searchSetting === 'SEARCH_ALL_COMPANIES') {
            return <BigX sourcePage={"disconnectionManagement:noResult"}
                         icon={<img src={NoResultsIcon} alt="No Data"/>}/>;
        }
        return <>
            <CustomDataGrid
                columns={columns}
                rows={filteredManagedCompaniesResponse}
                loading={isLoading}
                columnGroupingModel={columnGroupingModel}
                experimentalFeatures={{columnGrouping: true}}
                getRowId={(row: any) => row?.action_id ? row.action_id : row.codatCompanyId}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                className={styles.tableWrapper}
                getRowHeight={() => "auto"}
            />
        </>

    }

    return (
        <>
            <Box className={styles.container}>
                <Box className={styles.paper}>
                    <DisconnectionManagementFilters displayNullState={displayNullState}/>

                    <>
                        <Box className={styles.searchBar}>
                            <CompanySearch mode={searchSetting === 'SEARCH_ALL_COMPANIES' ? "online" : 'offline'}
                                           getSearchResults={getSearchResults}
                                           minCharsToFire={3}
                                           debounceTime={300}
                                           currentTab={filterType}
                                           offlineSearchData={managedCompaniesResponse}
                            />
                            {filterType === CONSTANTS.DISCONNECTION_MANAGEMENT_FILTERS.ACTION_REQUIRED && <Box>
                                <FormControl sx={{m: 1, minWidth: 80}} className={'search-setting-wrapper'}>
                                    <InputLabel id="search-setting-label">Search Setting</InputLabel>
                                    <Select
                                        labelId="search-setting-label"
                                        id="search-setting"
                                        data-testid={"search-setting"}
                                        name={'searchSetting'}
                                        value={searchSetting}
                                        label="Search Setting"
                                        onChange={handleChange}
                                    >
                                        {searchSettingOptions.map(({displayName, value}) => <MenuItem
                                            className={styles.searchSettingMenuItem}
                                            value={value}>{displayName}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Box>}
                        </Box>
                        {renderContent()}

                    </>

                </Box>
                <InactivateCompanyInstructions
                    open={openInactivateCompanyDialog}
                    onClose={handleInactivateCompanyDialogClose}
                    onConfirm={handleInactivateCompanyConfirm}
                    company={selectedCompany}/>
                <ActivateCompanyDialog
                    open={openActivateCompanyDialog}
                    onClose={handleActivateCompanyDialogClose}
                    onConfirm={handleActivateCompanyConfirm}/>

                <RenewalCompanyDialog
                    open={renewalDialogOpen}
                    onClose={handleCloseRenewalDialog}
                    company={selectedCompany}
                    fetchManagedCompaniesDetails={fetchManagedCompaniesDetails}
                    setCircularProgressLoading={setCircularProgressLoading}
                />
                <Backdrop open={isCircularProgressLoading} className={styles.circularProgress}>
                    <CircularProgress color="inherit"/>
                </Backdrop>
            </Box>
        </>
    )
        ;
};
